import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink, HelpCircle, AlertCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface QuickViewSheetLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: 'lg' | 'xl' | '640' | '2xl' | 'full';

  // Slot content
  title: React.ReactNode;
  subtitle?: string;
  badges?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;

  // State
  isLoading?: boolean;
  error?: Error | null;
  isEmpty?: boolean;
  emptyMessage?: string;

  // Navigation. Omit fullDetailPath when the sheet IS the full view (no
  // dedicated /:id page); the footer link is then hidden.
  fullDetailPath?: string;
  fullDetailLabel?: string;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

const SIZE_CLASSES = {
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '640': 'sm:max-w-[640px]',
  '2xl': 'sm:max-w-2xl',
  full: 'sm:max-w-[calc(100vw-280px)]',
} as const;

const KEYBOARD_HINTS_KEY = 'quickview-show-keyboard-hints';

export function QuickViewSheetLayout({
  open,
  onOpenChange,
  size = 'xl',
  title,
  subtitle,
  badges,
  actions,
  children,
  isLoading,
  error,
  isEmpty,
  emptyMessage = 'Record not found',
  fullDetailPath,
  fullDetailLabel = 'Open Full Details',
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
}: QuickViewSheetLayoutProps) {
  const navigate = useNavigate();
  const [showKeyboardHints, setShowKeyboardHints] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(KEYBOARD_HINTS_KEY) === 'true';
  });

  // Toggle keyboard hints
  const toggleKeyboardHints = () => {
    const newValue = !showKeyboardHints;
    setShowKeyboardHints(newValue);
    localStorage.setItem(KEYBOARD_HINTS_KEY, String(newValue));
  };

  // Handle "Open Full Details" click
  const handleOpenFullDetails = () => {
    onOpenChange(false);
    if (fullDetailPath) navigate(fullDetailPath);
  };

  // Render loading skeleton
  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className={cn('w-full', SIZE_CLASSES[size])}>
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-5 w-20" />
            </div>
          </SheetHeader>
          <div className="space-y-4 pt-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Render error state
  if (error) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className={cn('w-full', SIZE_CLASSES[size])}>
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Error Loading Record
            </SheetTitle>
          </SheetHeader>
          <div className="py-8 text-center">
            <p className="text-muted-foreground mb-4">{error.message}</p>
            {fullDetailPath ? (
              <Button variant="outline" onClick={handleOpenFullDetails}>
                <ExternalLink className="h-4 w-4 mr-2" />
                {fullDetailLabel}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Render empty state
  if (isEmpty) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className={cn('w-full', SIZE_CLASSES[size])}>
          <SheetHeader className="pb-4 border-b">
            <SheetTitle>Not Found</SheetTitle>
          </SheetHeader>
          <div className="py-8 text-center">
            <p className="text-muted-foreground">{emptyMessage}</p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn('w-full flex flex-col p-0', SIZE_CLASSES[size])}>
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            {/* Nav buttons + Title */}
            <div className="flex items-center gap-3 min-w-0">
              {(onPrevious || onNext) && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 rounded-md"
                    onClick={onPrevious}
                    disabled={!hasPrevious}
                    title="Previous (←)"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 rounded-md"
                    onClick={onNext}
                    disabled={!hasNext}
                    title="Next (→)"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <div className="min-w-0">
                <SheetTitle className="truncate text-base font-semibold">{title}</SheetTitle>
                {subtitle && (
                  <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                )}
              </div>
            </div>

            {/* Badges (padded so they clear the sheet close X) */}
            {badges && (
              <div className="flex flex-shrink-0 items-center gap-2 pr-7">
                {badges}
              </div>
            )}
          </div>
        </SheetHeader>

        {/* Quick Actions Bar */}
        {actions && (
          <div className="flex-shrink-0 border-b bg-background px-6 py-3">
            {actions}
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 items-center justify-between border-t bg-background px-6 py-3">
          {fullDetailPath ? (
            <Button
              variant="link"
              className="h-auto p-0 text-[13px] font-semibold text-primary"
              onClick={handleOpenFullDetails}
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              {fullDetailLabel}
            </Button>
          ) : (
            <span />
          )}

          {/* Keyboard hints toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={toggleKeyboardHints}
                >
                  <HelpCircle className={cn(
                    'h-4 w-4',
                    showKeyboardHints ? 'text-primary' : 'text-muted-foreground'
                  )} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="end">
                {showKeyboardHints ? (
                  <div className="text-xs space-y-1">
                    <p><kbd className="px-1 bg-muted rounded">←</kbd> Previous</p>
                    <p><kbd className="px-1 bg-muted rounded">→</kbd> Next</p>
                    <p><kbd className="px-1 bg-muted rounded">Esc</kbd> Close</p>
                  </div>
                ) : (
                  <p>Show keyboard shortcuts</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </SheetContent>
    </Sheet>
  );
}
